import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Lock, Unlock } from "lucide-react";
import { surveysAdmin } from "../../../lib/adminQueues";

const money = (c) => `$${(c / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/** Beta students: both survey moments, who is holding up their final installment, and a waive button. */
export const BetaStudentsTable = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => surveysAdmin.students().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const waive = async (r) => {
    if (!window.confirm(`Release ${r.student_name}'s final installment without the survey?`)) return;
    setBusy(r.campaign_id);
    try {
      await surveysAdmin.waive(r.campaign_id);
      toast.success(`Survey requirement waived for ${r.student_name}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't waive.");
    } finally {
      setBusy(null);
    }
  };

  if (!rows) return <p className="text-sm text-olive">Loading students…</p>;
  if (!rows.length) return <p className="rounded-xl border border-dashed border-border bg-bone p-5 text-sm text-olive">No live or closed campaigns yet.</p>;
  return (
    <ul className="divide-y divide-ink/10 rounded-xl border border-border bg-bone" data-testid="feedback-students">
      {rows.map((r) => (
        <li key={r.campaign_id} data-testid={`feedback-student-${r.campaign_id}`} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-ink">{r.student_name} <span className="font-normal text-olive">· {r.campaign_title}</span></p>
            <p className="text-[12px] text-olive">{r.status} · raised {money(r.raised_cents)} · paid out {money(r.paid_cents)}{!r.grant_landed && " · no grant yet"}</p>
          </div>
          <span data-testid={`feedback-student-submit-${r.campaign_id}`} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${r.submit_completed_at ? "bg-grass/15 text-grass" : "bg-ink/5 text-olive"}`}>
            {r.submit_completed_at ? <><CheckCircle2 className="h-3.5 w-3.5" /> Builder survey in</> : r.submit_skipped_at ? "Builder survey skipped" : "Builder survey not yet"}
          </span>
          {r.grant_completed_at ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-grass/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-grass"><CheckCircle2 className="h-3.5 w-3.5" /> Grant survey in · {new Date(r.grant_completed_at).toLocaleDateString()}</span>
          ) : r.waived_at ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-ink/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/70"><Unlock className="h-3.5 w-3.5" /> Waived by {r.waived_by}</span>
          ) : r.final_blocked ? (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow/40 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink"><Lock className="h-3.5 w-3.5" /> Final installment held</span>
              <button type="button" onClick={() => waive(r)} disabled={busy === r.campaign_id} data-testid={`feedback-waive-${r.campaign_id}`} className="rounded-full border border-ink/20 px-3 py-1.5 text-xs font-semibold text-ink hover:bg-ink hover:text-sand disabled:opacity-50">Waive</button>
            </>
          ) : (
            <span className="text-[12px] text-olive">Not required</span>
          )}
        </li>
      ))}
    </ul>
  );
};
